import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="riester-zulage-form"
export default class extends Controller {
	static targets = ["betrag", "steigerung", "kind", "datum", "datumWiederholend", "intervall", "bezeichnung", "infotext"]

	connect() {
		this.letzteAutomatischeBezeichnung = this.aktuelleBezeichnung()
		this.updateFelder()
		this.updateBezeichnung()
		this.updateInfotext()
	}

	updateFelder() {
		const freieZulage = this.freieZulageAusgewaehlt()
		const kinderzulage = this.kinderzulageAusgewaehlt()
		const berufseinsteigerbonus = this.berufseinsteigerbonusAusgewaehlt()
		const einmalig = this.einmaligAusgewaehlt()

		if (this.hasBetragTarget) {
			this.betragTarget.classList.toggle("hidden", !freieZulage)
		}

		if (this.hasIntervallTarget) {
			this.intervallTarget.classList.toggle("hidden", !freieZulage)
			const intervallSelect = this.intervallTarget.querySelector('select[name$="[intervall]"]')
			if (intervallSelect) intervallSelect.disabled = !freieZulage
		}

		if (this.hasSteigerungTarget) {
			this.steigerungTarget.classList.toggle("hidden", !freieZulage || einmalig)
		}

		if (this.hasKindTarget) {
			this.kindTarget.classList.toggle("hidden", !kinderzulage)
		}

		if (this.hasDatumWiederholendTarget) {
			this.datumWiederholendTarget.classList.toggle("hidden", berufseinsteigerbonus || einmalig)
		}

		this.updateInfotext()
	}

	freieZulageAusgewaehlt() {
		const select = this.element.querySelector('select[name$="[zulage_typ]"]')
		if (!select) return true

		return select.value == "frei" || select.value == "0"
	}

	einmaligAusgewaehlt() {
		const select = this.element.querySelector('select[name$="[intervall]"]')
		if (!select) return false

		return select.value == "einmalig" || select.value == "0"
	}

	kinderzulageAusgewaehlt() {
		const select = this.element.querySelector('select[name$="[zulage_typ]"]')
		if (!select) return false

		return select.value == "kinderzulage" || select.value == "2"
	}

	berufseinsteigerbonusAusgewaehlt() {
		const select = this.element.querySelector('select[name$="[zulage_typ]"]')
		if (!select) return false

		return select.value == "berufseinsteigerbonus" || select.value == "4"
	}

	updateKindZeitraum() {
		if (!this.kinderzulageAusgewaehlt()) return

		const kindSelect = this.element.querySelector('select[name$="[planung_kind_id]"]')
		if (!kindSelect) return

		const option = kindSelect.selectedOptions[0]
		if (!option) return

		const von = option.dataset.kindergeldVon
		const bis = option.dataset.kindergeldBis
		if (!von || !bis) return

		this.setzeStopdatumModusDatum()
		this.setzeDatumfeld("startdatum", von)
		this.setzeDatumfeld("stopdatum", bis)
	}

	updateBezeichnung() {
		if (!this.hasBezeichnungTarget) return

		const aktuelleBezeichnung = this.aktuelleBezeichnung()
		const neueBezeichnung = this.automatischeBezeichnung()
		const darfAktualisieren =
			aktuelleBezeichnung === "" || aktuelleBezeichnung === this.letzteAutomatischeBezeichnung

		if (darfAktualisieren && neueBezeichnung !== null) {
			this.bezeichnungTarget.value = neueBezeichnung
		}

		this.letzteAutomatischeBezeichnung = neueBezeichnung
	}

	bezeichnungGeaendert() {
		this.letzteAutomatischeBezeichnung = this.automatischeBezeichnung()
	}

	updateInfotext() {
		if (!this.hasInfotextTarget) return

		const infotext = this.infotexte()[this.ausgewaehlterZulageTyp()] || ""
		this.infotextTarget.textContent = infotext
		this.infotextTarget.classList.toggle("hidden", infotext === "")
	}

	setzeStopdatumModusDatum() {
		const modusSelect = this.element.querySelector('select[name$="[stopdatum_modus]"]')
		if (!modusSelect) return

		modusSelect.value = "0"
		modusSelect.dispatchEvent(new Event("change", { bubbles: true }))
	}

	setzeDatumfeld(praefix, isoDatum) {
		const datum = new Date(`${isoDatum}T00:00:00`)
		if (Number.isNaN(datum.getTime())) return

		const monat = String(datum.getMonth() + 1)
		const jahr = String(datum.getFullYear())

		const monatSelect = this.element.querySelector(`select[name="${praefix}_monat"]`)
		const jahrInput = this.element.querySelector(`input[name="${praefix}_jahr"]`)
		const hiddenInput = this.element.querySelector(`[name$="[${praefix}]"]`)

		if (monatSelect) {
			monatSelect.value = monat
			monatSelect.dispatchEvent(new Event("change", { bubbles: true }))
		}

		if (jahrInput) {
			jahrInput.value = jahr
			jahrInput.dispatchEvent(new Event("input", { bubbles: true }))
		}

		if (hiddenInput) hiddenInput.value = isoDatum
	}

	automatischeBezeichnung() {
		if (this.kinderzulageAusgewaehlt()) {
			const vorname = this.ausgewaehlterKindVorname()
			return vorname ? `Kinderzulage für ${vorname}` : "Kinderzulage"
		}

		return null
	}

	ausgewaehlterKindVorname() {
		const kindSelect = this.element.querySelector('select[name$="[planung_kind_id]"]')
		if (!kindSelect) return null

		const option = kindSelect.selectedOptions[0]
		if (!option) return null

		return option.dataset.vorname || null
	}

	aktuelleBezeichnung() {
		if (!this.hasBezeichnungTarget) return ""

		return this.bezeichnungTarget.value.trim()
	}

	ausgewaehlterZulageTyp() {
		const select = this.element.querySelector('select[name$="[zulage_typ]"]')
		if (!select) return "frei"

		const zulageTypByValue = {
			0: "frei",
			1: "grundzulage",
			2: "kinderzulage",
			4: "berufseinsteigerbonus",
		}

		return zulageTypByValue[select.value] || select.value
	}

	infotexte() {
		if (this._infotexte) return this._infotexte

		try {
			this._infotexte = JSON.parse(this.element.dataset.riesterZulageFormInfotexte || "{}")
		} catch {
			this._infotexte = {}
		}

		return this._infotexte
	}
}
